/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useContext} from 'react';
import {Text, TextSize} from '@Components/text';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {useComponentProp} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import {DEFAULT_PRESET_ID_FALLBACK} from './preset-selector-control.types';
import type {PresetOption, PresetSelectorDescriptor} from './preset-selector-control.types';
import styles from './preset-selector-control.module.scss';

interface PresetSelectorControlInnerProps extends EmailMakerControlProps {
  descriptor: PresetSelectorDescriptor;
}

function PresetSelectorControlInner({component, descriptor}: PresetSelectorControlInnerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);

  const rawProp = useComponentProp(component, descriptor.property);
  const selectedId: number = typeof rawProp === 'number' ? rawProp : (descriptor.defaultPresetId ?? DEFAULT_PRESET_ID_FALLBACK);

  function handlePresetClick(preset: PresetOption): void {
    if (!editor) {
      return;
    }
    component.set(descriptor.property, preset.id);
    if (descriptor.onPresetSelect) {
      descriptor.onPresetSelect(component, preset, editor);
    }
  }

  function renderPresetTile(preset: PresetOption): ReactElement {
    const isSelected = preset.id === selectedId;
    const className = `preset-wrapper js-comp-preset comp-${preset.compValues.join('-')} ${styles.presetWrapper} ${isSelected ? styles.presetWrapperSelected : ''}`;

    return (
      <button
        aria-pressed={isSelected}
        className={className}
        data-preset-id={preset.id}
        key={preset.id}
        onClick={(): void => {
          handlePresetClick(preset);
        }}
        type="button"
      >
        {preset.compValues.map((radius, idx) => (
          <div className={`preset-comp ${styles.presetComp}`} key={idx} style={{borderRadius: `${radius}${preset.classUnit}`}} />
        ))}
      </button>
    );
  }

  function renderLabel(): ReactElement | null {
    if (!descriptor.labelKey) {
      return null;
    }
    return <Text className={styles.label} size={TextSize.MD} val={window.i18next.t(descriptor.labelKey)} />;
  }

  return (
    <div className={styles.wrapper}>
      {renderLabel()}
      <div className={`comp-presets ${styles.presetList}`}>{descriptor.options.map((preset) => renderPresetTile(preset))}</div>
    </div>
  );
}

export function makePresetSelectorControl(descriptor: PresetSelectorDescriptor): EmailMakerControl {
  return createDescriptorControl(PresetSelectorControlInner, descriptor);
}
